/**
 * ToteCraft bag-studio — mockup preview, generate (async), design history.
 */
import { createStore } from './state/store.js';
import {
    parseMockupAngles,
    renderMockupView,
    resetRotatableMockupShell,
    resolveAngleIdForSide,
} from './ui/mockup-view.js';
import {
    bindStudioControls,
    previewModeAfterMockupPayload,
    shouldFallbackToFlatPreview,
} from './ui/controls.js';
import { bindGeneratePanel } from './ui/generate-panel.js';
import { bindStudioModeTabs } from './ui/studio-tabs.js';
import { renderDesignHistory } from './ui/design-history.js';
import { bindPrintSizePanel } from './ui/print-size-panel.js';
import { bindCheckoutToolbar } from './ui/checkout-toolbar.js';
import { bindAddTextPanel } from './ui/add-text-panel.js';
import { bindCopyToBack } from './ui/copy-to-back.js';
import { bindChangeSizePanel } from './ui/change-size.js';
import { bindConfirmModal } from './ui/confirm-modal.js';
import { bindBusyOverlay } from './ui/busy-overlay.js';
import {
    createProject,
    fetchProject,
    fetchPreview,
    fetchCartLines,
    generateDesign,
    suggestDesignBrief,
    selectDesign,
    uploadDesign,
    scalePrint,
    copyToBack,
    addText,
    changeSize,
} from './api/client.js';
import { pollProjectForDesign } from './api/poll.js';

(function () {
    'use strict';

    /** @type {Record<'front'|'back', string>} */
    const designUrls = { front: '', back: '' };

    /** @type {Record<string, unknown>|null} */
    let mockupMeta = null;

    /** @type {number} */
    let mockupCacheBust = 0;

    async function boot() {
        const root = document.getElementById('bag-studio-app');
        const previewHost = document.getElementById('bag-studio-preview-host');
        const historyHost = document.getElementById('bag-design-history');

        if (!root || !previewHost) {
            return;
        }

        let template = {};
        try {
            const jsonEl = document.getElementById('bag-studio-template-json');
            const raw = jsonEl && typeof jsonEl.textContent === 'string'
                ? jsonEl.textContent.trim()
                : (root.getAttribute('data-template') || '{}');
            template = JSON.parse(raw || '{}');
        } catch {
            template = {};
        }

        const attrTemplateId = (root.getAttribute('data-template-id') || '').trim();
        const payloadTemplateId = typeof template.template_id === 'string'
            ? template.template_id.trim()
            : '';
        const templateId = attrTemplateId || payloadTemplateId;
        let projectToken = root.getAttribute('data-project-token') || '';

        if (!templateId) {
            console.error('[BagStudio] Missing template_id on #bag-studio-app');
            previewHost.innerHTML = '<p class="text-danger small mb-0">Missing tote template. Please choose a template and try again.</p>';
            return;
        }

        const store = createStore({
            previewMode: 'closed',
            activeSide: 'front',
            templateId: templateId,
            template: template,
            projectToken: projectToken,
            canvasOz: 8,
            printSides: 'front_only',
            quota: null,
            designs: [],
            studioMode: 'generate',
            supportsMockup: false,
            mockupResolved: false,
            mockupSizeLabel: template.size_label || template.category_hint || '',
            anglesExpanded: false,
            activeAngleId: null,
            busy: false,
        });

        bindStudioControls(root, store);
        bindStudioModeTabs(root, store);
        const confirmModal = bindConfirmModal(root);
        const overlay = bindBusyOverlay(root);

        // Global mutation lock: one design-mutating request at a time.
        // `actionSeq` marks the latest request — stale completions (a response
        // arriving after a newer request started) are discarded instead of
        // overwriting newer project state.
        let actionSeq = 0;

        /**
         * @param {() => Promise<void>} action
         * @param {{ title?: string, detail?: string }} [meta]
         * @returns {Promise<void>}
         */
        async function withBusy(action, meta) {
            if (store.getState().busy) {
                throw new Error('Another action is still in progress. Please wait a moment.');
            }
            const seq = ++actionSeq;
            store.setState({ busy: true });
            overlay.show(
                'mutation',
                (meta && meta.title) || 'Working…',
                meta && typeof meta.detail === 'string'
                    ? meta.detail
                    : 'Please wait and stay on this page.',
            );
            try {
                await action();
            } finally {
                if (seq === actionSeq) {
                    store.setState({ busy: false });
                    overlay.hide('mutation');
                }
            }
        }

        const renderPreview = () => {
            const state = store.getState();
            renderMockupView(previewHost, {
                previewMode: state.previewMode === 'flat' ? 'flat' : 'closed',
                activeSide: /** @type {'front'|'back'} */ (state.activeSide || 'front'),
                printSides: state.printSides === 'front_back' ? 'front_back' : 'front_only',
                mockupMeta: mockupMeta,
                designUrls: { ...designUrls },
                template: /** @type {Record<string, unknown>} */ (state.template || template),
                cacheBust: mockupCacheBust,
                anglesExpanded: Boolean(state.anglesExpanded),
                activeAngleId: typeof state.activeAngleId === 'string' ? state.activeAngleId : null,
                onAnglesToggle: () => {
                    store.setState({ anglesExpanded: !store.getState().anglesExpanded });
                },
                onAngleChange: (angle) => {
                    store.setState({
                        activeAngleId: angle.id,
                        activeSide: angle.side === 'back' ? 'back' : 'front',
                    });
                },
            });
        };

        /** @type {string} */
        let lastPreviewKey = '';
        /** @type {string} */
        let lastHistoryKey = '';

        /**
         * Visual mockup identity — excludes busy/quota/oz so overlay locks do not
         * tear down <img> nodes (size-switch and generate were re-fetching mockups).
         * @param {Record<string, unknown>} state
         */
        function previewStateKey(state) {
            const angles = mockupMeta && Array.isArray(mockupMeta.angles) ? mockupMeta.angles : [];
            const angleSig = angles.map((row) => {
                const item = /** @type {Record<string, unknown>} */ (row || {});
                return String(item.id || '') + ':' + String(item.url || '');
            }).join(',');
            return [
                state.previewMode === 'flat' ? 'flat' : 'closed',
                state.activeSide === 'back' ? 'back' : 'front',
                state.printSides === 'front_back' ? 'front_back' : 'front_only',
                state.supportsMockup ? '1' : '0',
                String(state.activeAngleId || ''),
                state.anglesExpanded ? '1' : '0',
                String(mockupCacheBust),
                designUrls.front || '',
                designUrls.back || '',
                mockupMeta ? String(mockupMeta.front_closed_url || '') : '',
                angleSig,
            ].join('|');
        }

        /**
         * @param {Record<string, unknown>} state
         */
        function historyStateKey(state) {
            const designs = Array.isArray(state.designs) ? state.designs : [];
            const rows = designs.map((row) => {
                const item = /** @type {Record<string, unknown>} */ (row || {});
                return [
                    String(item.id || ''),
                    item.is_selected ? '1' : '0',
                    String(item.generation_status || ''),
                    String(item.side || ''),
                ].join(':');
            }).join(',');
            return String(state.activeSide === 'back' ? 'back' : 'front') + '|' + rows;
        }

        let previewPaused = false;

        function renderPreviewIfChanged() {
            if (previewPaused) {
                return false;
            }
            const key = previewStateKey(store.getState());
            if (key === lastPreviewKey) {
                return false;
            }
            lastPreviewKey = key;
            renderPreview();
            return true;
        }

        function refreshHistoryIfChanged() {
            const key = historyStateKey(store.getState());
            if (key === lastHistoryKey) {
                return;
            }
            lastHistoryKey = key;
            refreshHistory();
        }

        async function afterDesignChange() {
            mockupCacheBust = Date.now();
            previewPaused = true;
            try {
                await refreshMockupPayload(store);
            } finally {
                previewPaused = false;
            }
            lastPreviewKey = '';
            renderPreviewIfChanged();
            refreshHistoryIfChanged();
        }

        /**
         * @param {Record<string, unknown>|null|undefined} tpl
         */
        function sizeLabelFromTemplate(tpl) {
            if (!tpl || typeof tpl !== 'object') {
                return '';
            }
            return String(tpl.size_label_bilingual || tpl.size_label || tpl.category_hint || '');
        }

        /**
         * Keep the busy overlay up until the new mockup has actually painted.
         * @param {HTMLElement} host
         * @returns {Promise<void>}
         */
        function waitForVisibleMockup(host) {
            const img = host.querySelector('.tc-mockup-view__img:not(.tc-mockup-view__img--over)');
            if (!(img instanceof HTMLImageElement) || !img.getAttribute('src')) {
                return Promise.resolve();
            }
            if (img.complete && img.naturalWidth > 0) {
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const done = () => {
                    img.removeEventListener('load', done);
                    img.removeEventListener('error', done);
                    resolve();
                };
                img.addEventListener('load', done);
                img.addEventListener('error', done);
                window.setTimeout(done, 20000);
            });
        }

        /**
         * Apply change-size in place. A full reload used to abort the in-flight
         * mockup request and start a second compose of the same new size.
         * @param {Record<string, unknown>} payload
         */
        async function applySizeChange(payload) {
            const project = /** @type {Record<string, unknown>} */ (payload.project || {});
            const nextTemplate = /** @type {Record<string, unknown>} */ (payload.template || {});
            const nextId = String(nextTemplate.template_id || project.template_id || '').trim();
            if (!nextId) {
                throw new Error('Missing tote template after size change.');
            }

            const pricing = /** @type {Record<string, unknown>} */ (payload.pricing || {});
            const bilingual = sizeLabelFromTemplate(nextTemplate);
            const styleLabel = String(nextTemplate.style_label || 'Vertical Canvas Tote');

            root.setAttribute('data-template-id', nextId);
            root.setAttribute('data-project-token', String(project.token || ''));
            root.setAttribute('data-size-label', bilingual);
            if (typeof pricing.front_only === 'number') {
                root.setAttribute('data-price-front-only', String(pricing.front_only));
            }
            if (typeof pricing.front_back === 'number') {
                root.setAttribute('data-price-front-back', String(pricing.front_back));
            }
            if (pricing.matrix && typeof pricing.matrix === 'object') {
                root.setAttribute('data-pricing-matrix', JSON.stringify(pricing.matrix));
            }

            const jsonEl = document.getElementById('bag-studio-template-json');
            if (jsonEl) {
                jsonEl.textContent = JSON.stringify(nextTemplate);
            }

            const productLine = root.querySelector('[data-bag-studio-product-line]');
            if (productLine) {
                productLine.textContent = styleLabel + ' · ' + bilingual;
            }
            const previewLine = root.querySelector('[data-bag-studio-preview-line]');
            if (previewLine) {
                previewLine.textContent = 'Preview · Structure locked · ' + bilingual;
            }

            const redirect = typeof payload.redirect_url === 'string' ? payload.redirect_url.trim() : '';
            if (redirect && window.history && typeof window.history.replaceState === 'function') {
                window.history.replaceState({}, '', redirect);
            }

            previewPaused = true;
            try {
                mockupCacheBust = 0;
                resetRotatableMockupShell();
                applyProjectPayload(store, payload, {
                    templateId: nextId,
                    template: nextTemplate,
                    mockupSizeLabel: bilingual || store.getState().mockupSizeLabel,
                    activeAngleId: null,
                });
                await refreshMockupPayload(store);
            } finally {
                previewPaused = false;
            }

            lastPreviewKey = '';
            renderPreviewIfChanged();
            refreshHistoryIfChanged();
            await waitForVisibleMockup(previewHost);
        }

        bindChangeSizePanel(root, store, {
            changeSize,
            withBusy,
            confirm: (options) => confirmModal.confirm(options),
            onSizeChanged: (payload) => applySizeChange(payload),
        });

        function refreshHistory() {
            if (!historyHost) {
                return;
            }
            const state = store.getState();
            renderDesignHistory(
                historyHost,
                /** @type {Array<Record<string, unknown>>} */ (state.designs || []),
                /** @type {'front'|'back'} */ (state.activeSide || 'front'),
                async (designId, side) => {
                    try {
                        await withBusy(async () => {
                            const token = String(store.getState().projectToken || '');
                            const result = await selectDesign(token, designId, side);
                            applyProjectPayload(store, result);
                            await afterDesignChange();
                        }, {
                            title: 'Updating preview…',
                            detail: '',
                        });
                        refreshHistory();
                    } catch (err) {
                        panel.setStatus(err && err.message ? err.message : 'Could not select design.', true);
                    }
                },
                {
                    briefEnabled: panel.isBriefEnabled(),
                    onEditPlan: (design) => {
                        handleEditPlan(design).catch((err) => {
                            panel.setStatus(
                                err && err.message ? err.message : 'Could not load design plan.',
                                true,
                            );
                        });
                    },
                },
            );
            printSize.refresh();
        }

        /**
         * @returns {Record<string, unknown>|null}
         */
        function selectedDesignForActiveSide() {
            const state = store.getState();
            const side = state.activeSide === 'back' ? 'back' : 'front';
            const designs = Array.isArray(state.designs) ? state.designs : [];
            const selected = designs.find((d) => d && d.side === side && d.is_selected);
            return selected || null;
        }

        const printSize = bindPrintSizePanel(root, {
            getSelectedDesign: selectedDesignForActiveSide,
            getToken: () => String(store.getState().projectToken || ''),
            getActiveSide: () => (store.getState().activeSide === 'back' ? 'back' : 'front'),
            withBusy,
            scalePrint,
            onApplied: async (result) => {
                applyProjectPayload(store, result);
                await afterDesignChange();
                refreshHistory();
            },
        });

        /**
         * Phase 11.3 — load plan from history into Generate panel (new version on Generate).
         * @param {Record<string, unknown>} design
         */
        async function handleEditPlan(design) {
            store.setState({
                studioMode: 'generate',
                activeSide: design.side === 'back' ? 'back' : 'front',
            });

            const historyPanel = historyHost && historyHost.closest('details');
            if (historyPanel) {
                historyPanel.open = true;
            }

            const loaded = panel.loadFromDesign(design);
            if (!loaded.needsSuggest) {
                panel.focusBriefPanel();
                return;
            }

            await withBusy(async () => {
                panel.setStatus('No saved plan on this design — suggesting a fresh plan…', false);
                const token = String(store.getState().projectToken || '');
                if (!token) {
                    throw new Error('Design session not ready.');
                }
                const brief = await suggestDesignBrief(token, {
                    prompt: loaded.prompt,
                    style_code: loaded.style_code,
                    side: loaded.side,
                    has_reference: false,
                });
                panel.applyBrief(brief);
                panel.setStatus('Review the design plan, then Generate print for a new version.', false);
                panel.focusBriefPanel();
            }, {
                title: 'Loading design plan…',
                detail: 'This usually takes a few seconds.',
            });
        }

        const panel = bindGeneratePanel(root, store, (payload) => withBusy(async () => {
            const token = String(store.getState().projectToken || '');
            const result = await generateDesign(token, payload);

            if (result.generation && result.generation.status === 'pending' && result.generation.design_id) {
                panel.setStatus('Generating… queued. This may take up to a few minutes.', false);
                try {
                    const polled = await pollProjectForDesign(token, Number(result.generation.design_id));
                    if (polled) {
                        applyProjectPayload(store, polled);
                    }
                } catch (err) {
                    // Stop spinner with the failed design row even when poll throws.
                    if (err && err.payload) {
                        applyProjectPayload(store, err.payload);
                        refreshHistory();
                    }
                    throw err;
                }
            } else {
                applyProjectPayload(store, result);
            }

            await afterDesignChange();
            refreshHistory();
            panel.setStatus('Design ready — preview updated.', false);
        }, {
            title: 'Creating your print…',
            detail: 'Usually 30–90 seconds. Please stay on this page.',
        }), (payload) => withBusy(async () => {
            const token = String(store.getState().projectToken || '');
            const result = await uploadDesign(token, payload);
            applyProjectPayload(store, result);
            await afterDesignChange();
            refreshHistory();
            panel.setStatus('Design ready — preview updated.', false);
        }, {
            title: 'Fitting your photo…',
            detail: 'This usually takes a few seconds.',
        }), async (payload) => {
            const token = String(store.getState().projectToken || '');
            return suggestDesignBrief(token, payload);
        }, overlay);

        if (store.getState().quota) {
            panel.setQuota(store.getState().quota);
        }

        const copyPanel = bindCopyToBack(root, store, () => withBusy(async () => {
            const token = String(store.getState().projectToken || '');
            const result = await copyToBack(token);
            applyProjectPayload(store, result, { activeSide: 'back' });
            await afterDesignChange();
            refreshHistory();
            copyPanel.setStatus('Copied to the back. Each side can still be edited independently.', false);
        }, {
            title: 'Copying to the back…',
            detail: '',
        }));

        const textPanel = bindAddTextPanel(root, store, (payload) => withBusy(async () => {
            const token = String(store.getState().projectToken || '');
            const result = await addText(token, payload);
            applyProjectPayload(store, result);
            await afterDesignChange();
            refreshHistory();
            textPanel.setStatus('Text applied — preview updated.', false);
        }, {
            title: 'Placing your text…',
            detail: '',
        }));

        bindCheckoutToolbar(root, store, async (state) => {
            const token = String(state.projectToken || '');
            if (!token) {
                throw new Error('Design session not ready.');
            }
            if (typeof window.PurchaseCart === 'undefined') {
                throw new Error('Cart is unavailable. Please refresh the page.');
            }

            const payload = await fetchCartLines(token, {
                canvas_oz: state.canvasOz === 12 ? 12 : 8,
                print_sides: state.printSides === 'front_back' ? 'front_back' : 'front_only',
            });

            const lines = payload.cart_lines || [];
            if (!lines.length) {
                throw new Error('Could not build cart line for this tote.');
            }

            const result = window.PurchaseCart.addCustomizationLines(lines);
            if (!result.success) {
                throw new Error(result.message || 'Could not add to cart.');
            }
        });

        store.subscribe((state) => {
            if (shouldFallbackToFlatPreview(state)) {
                store.setState({ previewMode: 'flat' });
                return;
            }
            if (syncActiveAngleWithSide(store)) {
                return;
            }
            renderPreviewIfChanged();
            refreshHistoryIfChanged();
        });

        refreshHistoryIfChanged();

        try {
            if (projectToken) {
                try {
                    const existing = await fetchProject(projectToken);
                    applyProjectPayload(store, existing);
                } catch {
                    projectToken = '';
                    root.setAttribute('data-project-token', '');
                }
            }

            if (!projectToken) {
                const created = await createProject(templateId);
                applyProjectPayload(store, created);
                projectToken = String(store.getState().projectToken || '');
                if (projectToken) {
                    root.setAttribute('data-project-token', projectToken);
                }
            }

            await initDesignTextures(/** @type {Array<Record<string, unknown>>} */ (store.getState().designs || []));
            await refreshMockupPayload(store);
            renderPreviewIfChanged();
            refreshHistoryIfChanged();
        } catch (err) {
            console.error('[BagStudio] boot failed', err);
            if (!previewHost.querySelector('.tc-mockup-view')) {
                previewHost.innerHTML = '<p class="text-danger small mb-0">Could not load this tote preview. Please refresh and try again.</p>';
            }
        }

        window.BagStudio = {
            version: 'phase9-mockup-preview-9.5',
            templateId: store.getState().templateId,
            getState: () => store.getState(),
        };
    }

    /**
     * @param {{ setState: (patch: Record<string, unknown>) => void, getState: () => Record<string, unknown> }} store
     */
    async function refreshMockupPayload(store) {
        const token = String(store.getState().projectToken || '');
        if (!token) {
            mockupMeta = null;
            store.setState({ supportsMockup: false, mockupResolved: false });
            return;
        }

        try {
            const payload = await fetchPreview(token);
            const preview = /** @type {Record<string, unknown>} */ (payload.preview || {});
            const mockup = preview.mockup;
            mockupMeta = mockup && typeof mockup === 'object' ? /** @type {Record<string, unknown>} */ (mockup) : null;
            const supportsMockup = mockupMeta !== null;
            const prev = store.getState();
            const patch = {
                supportsMockup: supportsMockup,
                mockupResolved: true,
                mockupSizeLabel: mockupMeta && typeof mockupMeta.size_label === 'string'
                    ? mockupMeta.size_label
                    : prev.mockupSizeLabel,
            };
            const nextMode = previewModeAfterMockupPayload(prev, supportsMockup);
            if (nextMode) {
                patch.previewMode = nextMode;
            }
            store.setState(patch);
            syncDesignUrlsFromPreview(/** @type {Record<string, unknown>} */ (preview.designs || {}));
            syncActiveAngleWithSide(store);
        } catch (err) {
            console.error('[BagStudio] preview payload failed', err);
            mockupMeta = null;
            store.setState({
                supportsMockup: false,
                mockupResolved: true,
                previewMode: 'flat',
                activeAngleId: null,
            });
        }
    }

    /**
     * Keep activeAngleId aligned with Front/Back pills (Phase 9.5).
     * @param {{ setState: (patch: Record<string, unknown>) => void, getState: () => Record<string, unknown> }} store
     * @returns {boolean} true if setState was called (subscriber should wait for re-entry)
     */
    function syncActiveAngleWithSide(store) {
        const state = store.getState();
        const angles = parseMockupAngles(mockupMeta);
        const side = state.activeSide === 'back' ? 'back' : 'front';

        if (!angles.length) {
            if (state.activeAngleId) {
                store.setState({ activeAngleId: null });
                return true;
            }
            return false;
        }

        const current = angles.find((a) => a.id === state.activeAngleId);
        if (current && current.side === side) {
            return false;
        }

        const nextId = resolveAngleIdForSide(angles, side);
        if (!nextId || nextId === state.activeAngleId) {
            return false;
        }

        const next = angles.find((a) => a.id === nextId);
        store.setState({
            activeAngleId: nextId,
            activeSide: next && next.side === 'back' ? 'back' : 'front',
        });
        return true;
    }

    /**
     * @param {Record<string, unknown>} designs
     */
    function syncDesignUrlsFromPreview(designs) {
        if (typeof designs.front_preview_url === 'string' && designs.front_preview_url) {
            designUrls.front = designs.front_preview_url;
        }
        if (typeof designs.back_preview_url === 'string' && designs.back_preview_url) {
            designUrls.back = designs.back_preview_url;
        }
    }

    /**
     * @param {{ setState: (patch: Record<string, unknown>) => void, getState: () => Record<string, unknown> }} store
     * @param {Record<string, unknown>} payload
     * @param {Record<string, unknown>} [extra]
     */
    function applyProjectPayload(store, payload, extra) {
        const project = /** @type {Record<string, unknown>} */ (payload.project || {});
        const designs = Array.isArray(project.designs)
            ? project.designs
            : (store.getState().designs || []);
        store.setState(Object.assign({
            projectToken: project.token || store.getState().projectToken,
            templateId: project.template_id || store.getState().templateId,
            canvasOz: project.canvas_oz ? Number(project.canvas_oz) : store.getState().canvasOz,
            printSides: project.print_sides || store.getState().printSides,
            quota: payload.quota || store.getState().quota,
            designs: designs,
        }, extra || {}));
        syncDesignTexturesFromProject(/** @type {Array<Record<string, unknown>>} */ (designs));
    }

    /**
     * @param {Array<Record<string, unknown>>} designs
     */
    function syncDesignTexturesFromProject(designs) {
        designUrls.front = '';
        designUrls.back = '';
        designs.forEach((design) => {
            if (design.generation_status !== 'ok' || !design.preview_url || !design.is_selected) {
                return;
            }
            const side = design.side === 'back' ? 'back' : 'front';
            designUrls[side] = String(design.preview_url);
        });
    }

    /**
     * @param {Array<Record<string, unknown>>} designs
     */
    async function initDesignTextures(designs) {
        designUrls.front = '';
        designUrls.back = '';
        if (Array.isArray(designs) && designs.length > 0) {
            syncDesignTexturesFromProject(designs);
        }
    }

    let bootStarted = false;
    function startBoot() {
        const root = document.getElementById('bag-studio-app');
        if (bootStarted || (root && root.getAttribute('data-tc-studio-booted') === '1')) {
            return;
        }
        bootStarted = true;
        if (root) {
            root.setAttribute('data-tc-studio-booted', '1');
        }
        boot().catch((err) => console.error('[BagStudio] boot failed', err));
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startBoot);
    } else {
        startBoot();
    }
})();
